import {
  type AccessRequestOf,
  type ArgsOf,
  type RequestOf,
  isAccessRequestOf,
} from "../../../../access/types.ts";
import type { Permission } from "../../../../types/permission.ts";
import type {
  AwsSshAccesses,
  AwsSshAllAccess,
  AwsSshGroupAccess,
  AwsSshParentAccess,
  AwsSshSessionAccess,
} from "./aws.ts";
import type {
  AzureSshAccesses,
  AzureSshAllAccess,
  AzureSshGroupAccess,
  AzureSshParentAccess,
  AzureSshSessionAccess,
} from "./azure.ts";
import type {
  GcloudSshAccesses,
  GcloudSshAllAccess,
  GcloudSshGroupAccess,
  GcloudSshParentAccess,
  GcloudSshSessionAccess,
} from "./gcloud.ts";
import type {
  SelfHostedSshAccesses,
  SelfHostedSshAllAccess,
  SelfHostedSshGroupAccess,
  SelfHostedSshParentAccess,
  SelfHostedSshSessionAccess,
} from "./self-hosted.ts";
import type {
  UnknownSshAccesses,
  UnknownSshAllAccess,
  UnknownSshGroupAccess,
  UnknownSshParentAccess,
  UnknownSshSessionAccess,
} from "./unknown.ts";

export type SshSessionAccess =
  | AwsSshSessionAccess
  | AzureSshSessionAccess
  | GcloudSshSessionAccess
  | SelfHostedSshSessionAccess
  | UnknownSshSessionAccess;

export type SshGroupAccess =
  | AwsSshGroupAccess
  | AzureSshGroupAccess
  | GcloudSshGroupAccess
  | SelfHostedSshGroupAccess
  | UnknownSshGroupAccess;

export type SshAllAccess =
  | AwsSshAllAccess
  | AzureSshAllAccess
  | GcloudSshAllAccess
  | SelfHostedSshAllAccess
  | UnknownSshAllAccess;

export type SshParentAccess =
  | AwsSshParentAccess
  | AzureSshParentAccess
  | GcloudSshParentAccess
  | SelfHostedSshParentAccess
  | UnknownSshParentAccess;

export type SshAccesses =
  | AwsSshAccesses
  | AzureSshAccesses
  | GcloudSshAccesses
  | SelfHostedSshAccesses
  | UnknownSshAccesses;

export type SshAccess = SshAccesses[keyof SshAccesses];

export type SshAccessArgs = ArgsOf<SshAccess>;
export type SshSessionAccessArgs = ArgsOf<SshSessionAccess>;
export type SshGroupAccessArgs = ArgsOf<SshGroupAccess>;
export type SshAllAccessArgs = ArgsOf<SshAllAccess>;

export type SshSessionRequest = RequestOf<SshSessionAccess>;
export type SshGroupRequest = RequestOf<SshGroupAccess>;
export type SshParentRequest = RequestOf<SshParentAccess>;
export type SshAllRequest = RequestOf<SshAllAccess>;

export type SshSessionAccessRequest = AccessRequestOf<SshAccesses, "session">;

export type KnownProviderSshSessionRequest = Extract<
  SshSessionRequest,
  { permission: { resource: object } }
>;

export const isSshSessionRequest = (
  request: Permission
): request is SshSessionAccessRequest & { type: "ssh" } =>
  isAccessRequestOf("ssh", request) && request.access === "session";

export const isKnownProviderSshSessionRequest = (
  request: SshSessionRequest
): request is KnownProviderSshSessionRequest =>
  "resource" in request.permission;

export const isAzureSshSessionRequest = (
  request: SshSessionAccessRequest
): request is AccessRequestOf<AzureSshAccesses, "session"> =>
  request.permission.provider === "azure" && "resource" in request.permission;

export const isAwsSshSessionRequest = (
  request: SshSessionAccessRequest
): request is AccessRequestOf<AwsSshAccesses, "session"> =>
  request.permission.provider === "aws" && "resource" in request.permission;

export const isGcloudSshSessionRequest = (
  request: SshSessionAccessRequest
): request is AccessRequestOf<GcloudSshAccesses, "session"> =>
  request.permission.provider === "gcloud" && "resource" in request.permission;

export const isSelfHostedSshSessionRequest = (
  request: SshSessionAccessRequest
): request is AccessRequestOf<SelfHostedSshAccesses, "session"> =>
  request.permission.provider === "self-hosted" &&
  "resource" in request.permission;
